import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Shield, Smartphone } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

import { isLoginFormValid, getLoginErrorMessage } from "@/utils/loginUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/images/logo.png";

// ── Step 1: Email + Password ──────────────────────────────────────────────
function LoginStep({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedEmail = email.trim();
    const trimmedPassword = password.trim();
    if (!isLoginFormValid(trimmedEmail, trimmedPassword)) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email: trimmedEmail, password: trimmedPassword });
      onSuccess(res.data);
    } catch (err) {
      toast.error(getLoginErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <Label style={{ fontFamily: "'Poppins', sans-serif", fontSize: "13px", fontWeight: 600, color: "#374151" }}>Email</Label>
        <Input type="email" value={email} onChange={e => setEmail(e.target.value)}
          placeholder="agent@thebetterfare.com" required className="mt-1 h-11" />
      </div>
      <div>
        <Label style={{ fontFamily: "'Poppins', sans-serif", fontSize: "13px", fontWeight: 600, color: "#374151" }}>Password</Label>
        <Input type="password" value={password} onChange={e => setPassword(e.target.value)}
          required className="mt-1 h-11" placeholder="••••••••" />
      </div>
      <Button type="submit" disabled={loading || !isLoginFormValid(email, password)}
        style={{ background: "#6C3084", fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}
        className="w-full h-11 hover:opacity-90 text-white">
        {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Signing in...</> : "Sign In"}
      </Button>
    </form>
  );
}

// ── Step 2a: 2FA Setup ────────────────────────────────────────────────────
function TwoFASetupStep({ preAuthToken, agentEmail, onSuccess }) {
  const [qrCode, setQrCode] = useState(null);
  const [secret, setSecret] = useState(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  const fetchQR = async () => {
    setFetching(true);
    try {
      const res = await axios.post(`${API}/auth/2fa/setup`, { pre_auth_token: preAuthToken });
      setQrCode(res.data.qr_code);
      setSecret(res.data.secret);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate QR code — please log in again");
    } finally {
      setFetching(false);
    }
  };

  if (!qrCode && !fetching) fetchQR();

  const handleEnable = async (e) => {
    e.preventDefault();
    if (code.length !== 6) { toast.error("Enter the 6-digit code from your authenticator app"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/2fa/enable`, { pre_auth_token: preAuthToken, code: code.trim() });
      onSuccess(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid code — try again");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <p className="text-sm font-semibold text-amber-800 flex items-center gap-2">
          <Shield className="w-4 h-4" /> Two-Factor Authentication Required
        </p>
        <p className="text-xs text-amber-700 mt-1">Your account must be secured with 2FA. One-time setup.</p>
      </div>

      <div className="space-y-2">
        <p className="text-sm font-semibold text-gray-700">Step 1 — Install an authenticator app</p>
        <div className="flex gap-2 flex-wrap">
          {["Google Authenticator", "Microsoft Authenticator", "Authy"].map(app => (
            <span key={app} style={{ background: "#f3e8ff", color: "#6C3084" }} className="text-xs px-2 py-1 rounded font-medium">{app}</span>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-sm font-semibold text-gray-700">Step 2 — Scan this QR code</p>
        {fetching && (
          <div className="flex items-center justify-center h-40">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: "#6C3084" }} />
          </div>
        )}
        {qrCode && (
          <div className="flex flex-col items-center gap-3">
            <img src={qrCode} alt="QR Code" className="w-48 h-48 border-2 rounded-lg" style={{ borderColor: "#e9d5ff" }} />
            <button onClick={() => setShowSecret(!showSecret)} style={{ color: "#6C3084" }} className="text-xs hover:underline">
              {showSecret ? "Hide" : "Can't scan? Show"} manual entry code
            </button>
            {showSecret && secret && (
              <div className="p-3 bg-gray-50 rounded-lg border w-full text-center">
                <p className="text-xs text-gray-500 mb-1">Enter this code manually in your app:</p>
                <p className="font-mono font-bold tracking-widest text-sm break-all" style={{ color: "#6C3084" }}>{secret}</p>
              </div>
            )}
          </div>
        )}
      </div>

      <form onSubmit={handleEnable} className="space-y-3">
        <p className="text-sm font-semibold text-gray-700">Step 3 — Enter the 6-digit code to confirm</p>
        <Input
          value={code}
          onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          placeholder="000000"
          className="text-center font-mono text-2xl tracking-widest h-14"
          maxLength={6}
          autoFocus
        />
        <Button type="submit" disabled={loading || code.length !== 6}
          style={{ background: "#6C3084", fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}
          className="w-full h-11 hover:opacity-90 text-white">
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verifying...</> : "Enable 2FA & Sign In"}
        </Button>
      </form>
    </div>
  );
}

// ── Step 2b: 2FA Verify ───────────────────────────────────────────────────
function TwoFAVerifyStep({ preAuthToken, agentName, onSuccess, onBack }) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (code.length !== 6) { toast.error("Enter the 6-digit code"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/2fa/verify`, { pre_auth_token: preAuthToken, code: code.trim() });
      onSuccess(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid code");
      setCode("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleVerify} className="space-y-5">
      <div className="text-center">
        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: "#f3e8ff" }}>
          <Smartphone className="w-8 h-8" style={{ color: "#6C3084" }} />
        </div>
        <p className="text-sm text-gray-600">
          Hi <span className="font-semibold">{agentName}</span> — open your authenticator app and enter the 6-digit code.
        </p>
      </div>
      <Input
        value={code}
        onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
        placeholder="000000"
        className="text-center font-mono text-3xl tracking-widest h-16"
        style={{ borderColor: "#e9d5ff" }}
        maxLength={6}
        autoFocus
      />
      <Button type="submit" disabled={loading || code.length !== 6}
        style={{ background: "#6C3084", fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}
        className="w-full h-11 hover:opacity-90 text-white">
        {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verifying...</> : <><Shield className="w-4 h-4 mr-2" />Verify & Sign In</>}
      </Button>
      <button type="button" onClick={onBack} className="w-full text-xs text-gray-400 hover:text-gray-600">
        ← Back to login
      </button>
    </form>
  );
}

// ── Forgot Password ───────────────────────────────────────────────────────
function ForgotPassword({ onBack }) {
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const sendCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      toast.success("Reset code sent to your email");
      setStep("code");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Email not found");
    } finally { setLoading(false); }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { email, token: code, new_password: newPassword });
      toast.success("Password reset successfully — you can now log in");
      onBack();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid or expired code");
    } finally { setLoading(false); }
  };

  if (step === "email") return (
    <form onSubmit={sendCode} className="space-y-5">
      <p className="text-sm text-gray-500">Enter your email to receive a 6-digit reset code.</p>
      <div>
        <Label style={{ fontFamily: "'Poppins', sans-serif", fontSize: "13px", fontWeight: 600, color: "#374151" }}>Email</Label>
        <Input type="email" value={email} onChange={e => setEmail(e.target.value)} required className="mt-1 h-11" />
      </div>
      <Button type="submit" disabled={loading}
        style={{ background: "#6C3084", fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}
        className="w-full h-11 hover:opacity-90 text-white">
        {loading ? "Sending..." : "Send Reset Code"}
      </Button>
      <button type="button" onClick={onBack} className="w-full text-xs text-gray-400 hover:text-gray-600">← Back to login</button>
    </form>
  );

  return (
    <form onSubmit={resetPassword} className="space-y-5">
      <p className="text-sm text-gray-500">Enter the code from your email and choose a new password.</p>
      <div>
        <Label style={{ fontFamily: "'Poppins', sans-serif", fontSize: "13px", fontWeight: 600, color: "#374151" }}>6-Digit Code</Label>
        <Input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          placeholder="000000" className="mt-1 font-mono text-center text-xl tracking-widest h-12" maxLength={6} autoFocus />
      </div>
      <div>
        <Label style={{ fontFamily: "'Poppins', sans-serif", fontSize: "13px", fontWeight: 600, color: "#374151" }}>New Password</Label>
        <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
          placeholder="Min. 8 characters" required className="mt-1 h-11" />
      </div>
      <Button type="submit" disabled={loading}
        style={{ background: "#6C3084", fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}
        className="w-full h-11 hover:opacity-90 text-white">
        {loading ? "Resetting..." : "Reset Password"}
      </Button>
      <button type="button" onClick={onBack} className="w-full text-xs text-gray-400 hover:text-gray-600">← Back to login</button>
    </form>
  );
}

// ── Main AgentLogin Component ─────────────────────────────────────────────
export default function AgentLogin() {
  const { loginWithToken, token, agent } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (token && agent) {
      navigate("/dashboard");
    }
  }, [token, agent]);

  const [screen, setScreen] = useState("login");
  const [preAuthToken, setPreAuthToken] = useState(null);
  const [agentInfo, setAgentInfo] = useState(null);

  const handleLoginSuccess = (data) => {
    setAgentInfo(data.agent);
    setPreAuthToken(data.pre_auth_token);
    if (data.status === "2fa_setup_required") {
      setScreen("2fa_setup");
    } else if (data.status === "2fa_required") {
      setScreen("2fa_verify");
    } else if (data.token) {
      loginWithToken(data.token, data.agent);
      navigate("/dashboard");
    }
  };

  const handleTwoFASuccess = (data) => {
    loginWithToken(data.token, data.agent);
    navigate("/dashboard");
  };

  const titles = {
    login: "Agent Login",
    "2fa_setup": "Set Up Two-Factor Auth",
    "2fa_verify": "Two-Factor Verification",
    forgot: "Reset Password",
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "#f9f7fc" }}>
      <div className="w-full max-w-md">

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Top accent bar */}
          <div style={{ height: "4px", background: "linear-gradient(90deg, #6C3084 0%, #4A90D9 50%, #25D366 100%)" }} />

          <div className="p-8">
            {/* Logo + Title */}
            <div className="text-center mb-8">
              <img src={LOGO_URL} alt="The Better Fare" className="h-14 w-auto mx-auto mb-4 object-contain" />
              <h1 style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: "1.25rem", color: "#1a1a1a" }}>
                {titles[screen]}
              </h1>
             
            </div>

            {screen === "login" && (
              <>
                <LoginStep onSuccess={handleLoginSuccess} />
                <div className="text-center mt-5">
                  <button onClick={() => setScreen("forgot")}
                    style={{ color: "#6C3084", fontFamily: "'Poppins', sans-serif", fontSize: "12px" }}
                    className="hover:underline">
                    Forgot password?
                  </button>
                </div>
              </>
            )}

            {screen === "2fa_setup" && (
              <TwoFASetupStep
                preAuthToken={preAuthToken}
                agentEmail={agentInfo?.email}
                onSuccess={handleTwoFASuccess}
              />
            )}

            {screen === "2fa_verify" && (
              <TwoFAVerifyStep
                preAuthToken={preAuthToken}
                agentName={agentInfo?.name}
                onSuccess={handleTwoFASuccess}
                onBack={() => setScreen("login")}
              />
            )}

            {screen === "forgot" && (
              <ForgotPassword onBack={() => setScreen("login")} />
            )}
          </div>

          {/* Footer */}
          <div style={{ background: "#1f2937", padding: "12px 32px", textAlign: "center" }}>
            <p style={{ margin: 0, fontFamily: "'Poppins', sans-serif", fontSize: "11px", color: "rgba(255,255,255,0.45)" }}>
              The Better Fare &nbsp;&middot;&nbsp; KvK 88159620 &nbsp;&middot;&nbsp; www.thebetterfare.com
            </p>
          </div>
        </div>

      </div>
    </div>
  );
}
